function clamp (val, min = 0, max = 1) {
  return Math.min(max, Math.max(min, val));
}

/**
 * Holds information about a color
 */
export default class Color {
  /**
   * Constructor.
   *
   * @param  {Number} r The red value
   * @param  {Number} g The green value
   * @param  {Number} b The blue value
   * @param  {Number} a The alpha value
   */
  constructor (r, g, b, a = 1) {
    if (!(this instanceof Color)) {
      return new Color(r, g, b, a);
    }

    this._rgb = [r, g, b];
    this._hex = Color.rgbToHex(this._rgb);
    this._hsl = Color.rgbToHsl(this._rgb);
    this._alpha = Math.max(0, Math.min(1, a));
  }

  /**
   * Make a clone of this color
   *
   * @returns {Color}
   */
  clone () {
    return new Color(this.red(), this.green(), this.blue(), this.alpha());
  }

  /**
   * Retrieve the color in RGB
   *
   * @return {Array}
   */
  rgb () {
    return this._rgb;
  }

  /**
   * Retrieve the color in RGB, including the alhpa
   *
   * @return {Array}
   */
  rgba () {
    return this.rgb().concat(this.alpha());
  }

  /**
   * Retrieve the color as HEX
   *
   * @return {String}
   */
  hex () {
    return this._hex;
  }

  /**
   * Retrieve the color in HSL
   *
   * @return {Array}
   */
  hsl () {
    return this._hsl;
  }

  /**
   * Retrieve the color as HSL, including the alpha
   *
   * @return {Array}
   */
  hsla () {
    return this.hsl().concat(this.alpha());
  }

  /**
   * Retrieve the red value
   *
   * @return {Number}
   */
  red () {
    return this._rgb[0];
  }

  /**
   * Retrieve the green value
   *
   * @return {Number}
   */
  green () {
    return this._rgb[1];
  }

  /**
   * Retrieve the blue value
   *
   * @return {Number}
   */
  blue () {
    return this._rgb[2];
  }

  /**
   * Retrieve the alpha value
   *
   * @return {Number}
   */
  alpha () {
    return this._alpha;
  }

  /**
   * Check if the color is light
   *
   * @return {Number} A contrast value between 0 and 255
   */
  light () {
    // HSP (Highly Sensitive Poo) equation from http://alienryderflex.com/hsp.html
    const hsp = Math.sqrt(
      0.299 * Math.pow(this.red(), 2) +
      0.587 * Math.pow(this.green(), 2) +
      0.114 * Math.pow(this.blue(), 2)
    );

    // Using the HSP value, determine whether the color is light or dark
    return (hsp > 127.5);
  }

  contrast (color2) {
    // http://www.w3.org/TR/WCAG20/#contrast-ratiodef
    var lum1 = this.luminance();
    var lum2 = color2.luminance();

    if (lum1 > lum2) {
      return (lum1 + 0.05) / (lum2 + 0.05);
    }

    return (lum2 + 0.05) / (lum1 + 0.05);
  }

  luminance () {
    // http://www.w3.org/TR/WCAG20/#relativeluminancedef
    const rgb = this.rgba();
    const lum = [];
    for (let i = 0; i < rgb.length; i++) {
      const chan = rgb[i] / 255;
      lum[i] = (chan <= 0.03928) ? chan / 12.92 : Math.pow(((chan + 0.055) / 1.055), 2.4);
    }

    return 0.2126 * lum[0] + 0.7152 * lum[1] + 0.0722 * lum[2];
  }

  /**
   * Retrieve the color hue
   *
   * @return {Number}
   */
  hue () {
    return this._hsl[0];
  }

  /**
   * Retrieve the color saturation
   *
   * @return {Number}
   */
  saturation () {
    return this._hsl[1];
  }

  /**
   * Retrieve the color lightness
   *
   * @return {Number}
   */
  lightness () {
    return this._hsl[2];
  }

  /**
   * Blends this color with another by overlaying this color with a new one
   *
   * @param  {Color} overlay The color to be overlayed on this one
   *
   * @return {Color}
   */
  blend (overlay) {
    const a0 = overlay.alpha();
    const a1 = this.alpha();
    const r0 = overlay.red();
    const r1 = this.red();
    const g0 = overlay.green();
    const g1 = this.green();
    const b0 = overlay.blue();
    const b1 = this.blue();

    // a01 = (1 - a0)·a1 + a0
    const a01 = Math.round((1 - a0) * a1 + a0);
    // r01 = ((1 - a0)·a1·r1 + a0·r0) / a01
    const r01 = Math.round(((1 - a0) * a1 * r1 + a0 * r0) / a01);
    // g01 = ((1 - a0)·a1·g1 + a0·g0) / a01
    const g01 = Math.round(((1 - a0) * a1 * g1 + a0 * g0) / a01);
    // b01 = ((1 - a0)·a1·b1 + a0·b0) / a01
    const b01 = Math.round(((1 - a0) * a1 * b1 + a0 * b0) / a01);

    return new Color(r01, g01, b01, a01);
  }

  mix (mixinColor, weight) {
    // ported from sass implementation in C
    // https://github.com/sass/libsass/blob/0e6b4a2850092356aa3ece07c6b249f0221caced/functions.cpp#L209
    if (!mixinColor || !mixinColor.rgb()) {
      throw new Error('Argument to "mix" was not a Color instance, but rather an instance of ' + typeof mixinColor);
    }
    var color1 = mixinColor;
    var color2 = this;
    var p = weight === undefined ? 0.5 : weight;

    var w = 2 * p - 1;
    var a = color1.alpha() - color2.alpha();

    var w1 = (((w * a === -1) ? w : (w + a) / (1 + w * a)) + 1) / 2.0;
    var w2 = 1 - w1;

    return Color([
      w1 * color1.red() + w2 * color2.red(),
      w1 * color1.green() + w2 * color2.green(),
      w1 * color1.blue() + w2 * color2.blue(),
      color1.alpha() * p + color2.alpha() * (1 - p)
    ]);
  }

  /**
   * [darken description]
   * @param  {Number} amount [description]
   * @return {[type]}        [description]
   */
  darken (amount = 10) {
    const hsl = this.hsl();
    hsl[2] -= amount / 100;
    hsl[2] = clamp(hsl[2]);
    return Color.fromRgb(Color.hslToRgb(hsl));
  }

  /**
   * [lighten description]
   * @param  {Number} amount [description]
   * @return {[type]}        [description]
   */
  lighten (amount = 10) {
    const hsl = this.hsl();
    hsl[2] += amount / 100;
    hsl[2] = clamp(hsl[2]);
    return Color.fromRgb(Color.hslToRgb(hsl));
  }

  /**
   * Clone this color but with a different alhpa value
   *
   * @return {Color}
   */
  withAlpha (alpha) {
    return new Color(this.red(), this.green(), this.blue(), alpha);
  }

  /**
   * Format the color as a CSS color value
   *
   * @param  {String} format The desired css color format
   *
   * @return {String}
   */
  toCss (format = 'rgb') {
    switch (format) {
      case 'hex': return `#${this.hex()}`;
      case 'rgb': return `rgb(${this.rgb().join(', ')})`;
      case 'rgba': return `rgba(${this.rgba().join(', ')})`;
      case 'hsl': return `hsl(${this.hsl().join(', ')})`;
      case 'hsla': return `hsla(${this.hsla().join(', ')})`;
    }
  }

  /**
   * Format the color to a string
   *
   * @return {String}
   */
  toString () {
    return this.hex();
  }

  /**
   * Parse a color from a variety of formats
   *
   * @param  {String} color A color to be parsed in to a new Color object
   *
   * @return {Color}
   */
  static parse (color) {
    if (typeof color === 'string') {
      return Color.fromHex(color);
    }

    throw new Error(`Unrecognized color string '${color}'`);
  }

  /**
   * Create a color from a hex code
   *
   * @param  {String} hex A color hex code
   *
   * @return {Color}
   */
  static fromHex (hex) {
    return Color.fromRgb(Color.hexToRgb(hex));
  }

  /**
   * Create a color from an rgb array
   *
   * @param  {Array} rgb An RGB array
   *
   * @return {Color}
   */
  static fromRgb (rgb) {
    if (rgb.length === 4) {
      return new Color(rgb[0], rgb[1], rgb[2], rgb[3]);
    }

    return new Color(rgb[0], rgb[1], rgb[2]);
  }

  /**
   * Convert a hex code to an rgb array
   *
   * @param  {String} hex A color hex code
   *
   * @return {Array}
   */
  static hexToRgb (hex) {
    hex = hex.replace('#', '');

    if (hex.length === 3) {
      hex = hex.split('').map(function (num) {
        return num.repeat(2);
      }).join('');
    }

    return [
      parseInt(hex.substr(0, 2), 16),
      parseInt(hex.substr(2, 2), 16),
      parseInt(hex.substr(4, 2), 16)
    ];
  }

  /**
   * Convert an rgb array to a hex code
   *
   * @param  {Array} rgb An RGB array
   *
   * @return {String}
   */
  static rgbToHex (rgb) {
    let hex = Color.toHex(rgb[0]) + Color.toHex(rgb[1]) + Color.toHex(rgb[2]);

    if (rgb.length === 4) {
      let a = Math.round(rgb[3] * 255).toString(16);
      return hex + ('00' + a).substr(-2);
    }

    return hex;
  }

  /**
   * Convert an rgb array to an hsl array
   *
   * @param  {Array} rgb An RGB array
   *
   * @return {Array}
   */
  static rgbToHsl (rgb) {
    let r = rgb[0] / 255;
    let g = rgb[1] / 255;
    let b = rgb[2] / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h; let s; let l = (max + min) / 2;

    if (max === min) {
      h = s = 0;
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }
      h /= 6;
    }

    const hsl = [h, s, l];

    if (rgb.length === 4) {
      hsl.push(rgb[3]);
    }

    return hsl;
  }

  /**
   * Convert an hsl array to an rgb array
   *
   * @param  {Array} hsl An HSL array
   *
   * @return {Array}
   */
  static hslToRgb (hsl) {
    let r, g, b;
    const h = hsl[0];
    const s = hsl[1];
    const l = hsl[2];

    if (s === 0) {
      r = g = b = l; // achromatic
    } else {
      const hue2rgb = function hue2rgb (p, q, t) {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
      };

      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;

      r = hue2rgb(p, q, h + 1 / 3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1 / 3);
    }

    const rgb = [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];

    if (hsl.length === 4) {
      rgb.push(hsl[3]);
    }

    return rgb;
  }

  /**
   * Convert a number to a hexadecimal value
   *
   * @param  {Number} num The number to convert
   *
   * @return {String}
   */
  static toHex (num) {
    let hex = Number(Math.max(0, Math.min(255, num))).toString(16);
    return ('00' + hex).substr(-2);
  }
}

Color.WHITE = new Color(255, 255, 255, 1);
Color.BLACK = new Color(0, 0, 0, 1);
Color.TRANSPARENT = new Color(0, 0, 0, 0);
